

interface AlertProps {
  type: 'success' | 'error' | 'warning' | 'info'
  title?: string
  message: string
  dismissible?: boolean
  onDismiss?: () => void
  className?: string
}

const ALERT_CONFIGS = {
  success: {
    container: 'bg-brand-primary-50 border-l-4 border-brand-primary-500 p-4 rounded-lg',
    icon: 'text-brand-primary-500',
    title: 'text-brand-primary-700',
    message: 'text-brand-primary-600',
    closeButton: 'text-brand-primary-500 hover:bg-brand-primary-100 focus:ring-brand-primary-500 focus:ring-offset-brand-surface-50',
    svgPath:
      'M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z',
  },
  error: {
    container: 'bg-red-50 border-l-4 border-red-400 p-4 rounded-lg',
    icon: 'text-red-500',
    title: 'text-red-800',
    message: 'text-red-700',
    closeButton: 'text-red-500 hover:bg-red-100 focus:ring-red-500 focus:ring-offset-brand-surface-50',
    svgPath: 'M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z',
  },
  warning: {
    container: 'bg-brand-warning-50 border-l-4 border-brand-warning-500 p-4 rounded-lg',
    icon: 'text-brand-warning-600',
    title: 'text-brand-warning-700',
    message: 'text-brand-warning-600',
    closeButton: 'text-brand-warning-600 hover:bg-brand-warning-100 focus:ring-brand-warning-500 focus:ring-offset-brand-surface-50',
    svgPath: 'M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z',
  },
  info: {
    container: 'bg-brand-surface-100 border-l-4 border-brand-accent-500 p-4 rounded-lg',
    icon: 'text-brand-accent-500',
    title: 'text-brand-accent-700',
    message: 'text-brand-accent-600',
    closeButton: 'text-brand-accent-500 hover:bg-brand-accent-100 focus:ring-brand-accent-500 focus:ring-offset-brand-surface-50',
    svgPath: 'M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z',
  },
}

export default function Alert({
  type,
  title,
  message,
  dismissible = false,
  onDismiss,
  className = '',
}: AlertProps) {
  const config = ALERT_CONFIGS[type]

  return (
    <div className={`${config.container} ${className}`}>
      <div className="flex">
        <div className="flex-shrink-0">
          <svg
            className={`h-5 w-5 ${config.icon}`}
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d={config.svgPath}
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="ml-3 flex-1">
          {title && (
            <h3 className={`text-sm font-medium ${config.title}`}>
              {title}
            </h3>
          )}
          <div className={`text-sm ${config.message} ${title ? 'mt-2' : ''}`}>
            {message}
          </div>
        </div>
        {dismissible && (
          <div className="ml-auto pl-3">
            <div className="-mx-1.5 -my-1.5">
              <button
                type="button"
                className={`inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2 ${config.closeButton}`}
                onClick={onDismiss}
              >
                <span className="sr-only">Dismiss</span>
                <svg
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}